import React, {
    memo,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useReducer,
    useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import moment from 'moment';
import { Button, Switch, Text, Tooltip, useTheme } from '@Sage/hoi-poi-ui';
import {
    BetaBadge,
    COPILOT_CHAT_FEATURES,
    COPILOT_CHAT_INTERACTION_TYPES,
    CopilotComment as Comment,
    CopilotContext,
    CopilotLoader,
    CopilotModal,
} from '@Sage/ssm-web5';

import { EntityDetailActions } from 'actions';
import SageCopilot from 'components/icons/SageCopilot';
import useEmailEditor from 'containers/components/EmailEditor/hooks/useEmailEditor';
import { WidgetContentLayout, WidgetLayout } from 'containers/components/widgets/Layouts';
import useCopilot from 'hooks/useCopilot';
import metadata from 'lib/metadata';
import { ChatGPTService } from 'services';
import { isToday } from 'utils/dates';
import { getLiteral, getLiteralWithParameters } from 'utils/getLiteral';
import { logEvent } from 'utils/tracking';
import EmptySummary from './components/EmptySummary';
import ErrorScreen from './components/ErrorScreen';
import Suggestions from './components/Suggestions';

import './styles.scss';

const OPEN_COPILOT_CHAT = 'OPEN_COPILOT_CHAT';
const REFRESH_GOLDEN_MINUTE = 'REFRESH_GOLDEN_MINUTE';

// State action types
const WIDGET_ACTIONS = {
    SET_SUGGESTIONS: 'SET_SUGGESTIONS',
    SET_LOADING_SUGGESTIONS: 'SET_LOADING_SUGGESTIONS',
    SET_DEBUG_MODE: 'SET_DEBUG_MODE',
    RESET_FOR_REFRESH: 'RESET_FOR_REFRESH',
    FINISH_GOLDEN_MINUTE: 'FINISH_GOLDEN_MINUTE',
    ERROR_GOLDEN_MINUTE: 'ERROR_GOLDEN_MINUTE',
    OPEN_MODAL: 'OPEN_MODAL',
    CLOSE_MODAL: 'CLOSE_MODAL',
    SET_CHAT_PROPS: 'SET_CHAT_PROPS',
};

const widgetReducer = (state, action) => {
    switch (action.type) {
        case WIDGET_ACTIONS.SET_SUGGESTIONS:
            return { ...state, suggestions: action.payload };
        case WIDGET_ACTIONS.SET_LOADING_SUGGESTIONS:
            return { ...state, isLoadingSuggestions: action.payload };
        case WIDGET_ACTIONS.SET_DEBUG_MODE:
            return { ...state, isDebugMode: action.payload };
        case WIDGET_ACTIONS.RESET_FOR_REFRESH:
            return {
                ...state,
                suggestions: [],
                isLoadingGoldenMinute: true,
                isOpen: true,
                isError: false,
            };
        case WIDGET_ACTIONS.FINISH_GOLDEN_MINUTE:
            return {
                ...state,
                textWidget: action.payload.text,
                isLoadingGoldenMinute: false,
            };
        case WIDGET_ACTIONS.ERROR_GOLDEN_MINUTE:
            return {
                ...state,
                isLoadingGoldenMinute: false,
                isError: true,
            };
        case WIDGET_ACTIONS.OPEN_MODAL:
            return {
                ...state,
                isOpen: true,
            };
        case WIDGET_ACTIONS.CLOSE_MODAL:
            return {
                ...state,
                isOpen: false,
            };
        case WIDGET_ACTIONS.SET_CHAT_PROPS:
            return {
                ...state,
                chatProps: action.payload,
            };
        default:
            return state;
    }
};

const GoldenMinuteWidget = memo(
    ({
        actionsConfig,
        data,
        disableChatInput = false,
        entity,
        id,
        literals,
        loading,
        setSize,
        subEntity,
        tracking,
    }) => {
        // ========================================
        // HOOKS (CONTEXT, THEME, CUSTOM HOOKS)
        // ========================================
        const dispatch = useDispatch();
        const theme = useTheme();
        const { chats, clearChat, featureMethods, getChatState, getStreamReader } =
            useContext(CopilotContext);
        const { canUseEmail, mailContent } = useEmailEditor();

        // ========================================
        // REDUX STATE & SELECTORS
        // ========================================
        const userData = useSelector((state) => state.config.userData || {});

        // ========================================
        // LOCAL STATE
        // ========================================
        const defaultChatProps = useMemo(() => {
            return {
                defaultFirstInteraction: {
                    text: data.goldenMinuteSummary,
                    creationDate: data?.goldenMinuteDate,
                },
                enableVoiceInput: true,
                interactions: !disableChatInput
                    ? [
                          {
                              item: {
                                  content: {
                                      entity: entity.trueName,
                                      entityId: id,
                                  },
                                  type: COPILOT_CHAT_INTERACTION_TYPES.PARAMS,
                              },
                          },
                      ]
                    : [],
                session: {
                    features: [COPILOT_CHAT_FEATURES.ENTITY_CHAT],
                },
                overrides: { disableChatInput, disableAgentSwitcher: true },
            };
        }, [
            data?.goldenMinuteDate,
            data.goldenMinuteSummary,
            disableChatInput,
            entity.trueName,
            id,
        ]);

        const [state, dispatchLocal] = useReducer(widgetReducer, {
            chatProps: defaultChatProps,
            isDebugMode: false,
            isError: false,
            isLoadingGoldenMinute: false,
            isLoadingSuggestions: false,
            isOpen: false,
            suggestions: [],
            textWidget: data.goldenMinuteSummary || '',
        });

        const {
            chatProps,
            isDebugMode,
            isError,
            isLoadingGoldenMinute,
            isLoadingSuggestions,
            isOpen,
            suggestions,
            textWidget,
        } = state;

        const hasInitializedDebugMode = useRef(false);
        const latestCopilotAnswerRef = useRef('');
        const widgetCommentBoxRef = useRef(null);

        // ========================================
        // DERIVED STATE & MEMOIZATION
        // ========================================
        const chatId = useMemo(() => {
            let chatId = `${entity.entity}-${id}`;
            if (subEntity?.entity) chatId = `${chatId}-${subEntity.entity}`;
            return chatId;
        }, [entity?.entity, id, subEntity?.entity]);

        const chatState = useMemo(() => chats?.[chatId] ?? {}, [chats, chatId]);
        const { chat, chatSession } = chatState;

        const isOutdated = useMemo(() => {
            if (!data.goldenMinuteDate) return false;
            const momentDate = moment(data.goldenMinuteDate, 'DD/MM/YYYY HH:mm:ss');
            const momentNow = moment();
            const elapsedHours = momentNow.diff(momentDate, 'hours');
            return elapsedHours >= 24;
        }, [data.goldenMinuteDate]);

        const shouldShowSuggestions = useMemo(() => {
            return !disableChatInput;
        }, [disableChatInput]);

        const latestFinishedAssistantSummary = useMemo(() => {
            if (!chat) return '';

            const assistantMessages = Object.values(chat).filter(
                (item) =>
                    item?.role === 'assistant' &&
                    item?.isFinished &&
                    typeof item?.content === 'string',
            );

            return assistantMessages.at(-1)?.content?.trim() || '';
        }, [chat]);

        // ========================================
        // EVENT HANDLERS & CALLBACKS
        // ========================================
        const goldenMinuteDebugger = useCallback(
            (payload) => {
                if (!isDebugMode) return;
                if (payload && typeof payload === 'object' && Object.entries(payload).length) {
                    console.info(
                        `%cCOPILOT_GOLDEN_MINUTE:\n${JSON.stringify(payload)}`,
                        'color: olivedrab',
                    );
                }
            },
            [isDebugMode],
        );

        const updateGoldenMinuteLocally = useCallback(
            (text) => {
                const date = moment().format('DD/MM/YYYY HH:mm:ss');
                // We update the detail data so the next time we use this data it will have goldenMinute info.
                dispatch(
                    EntityDetailActions.updateDetailFields(entity, id, {
                        goldenMinuteSummary: text,
                        goldenMinuteDate: date,
                    }),
                );
            },
            [dispatch, entity, id],
        );

        const getDanaChatSuggestions = useCallback(() => {
            if (disableChatInput) return;
            const props = { entity, id };
            if (chatSession?.id) props.conversationId = chatSession.id;
            dispatchLocal({ type: WIDGET_ACTIONS.SET_LOADING_SUGGESTIONS, payload: true });
            ChatGPTService.getDanaChatSuggestions(props)
                .then((response) => {
                    if (!response?.length) return;
                    dispatchLocal({ type: WIDGET_ACTIONS.SET_SUGGESTIONS, payload: response });
                })
                .finally(() =>
                    dispatchLocal({
                        type: WIDGET_ACTIONS.SET_LOADING_SUGGESTIONS,
                        payload: false,
                    }),
                );
        }, [disableChatInput, entity, id, chatSession?.id]);

        const resolveSummaryText = useCallback(
            (payload) => {
                if (typeof payload === 'string') return payload.trim();

                const payloadText = payload?.content || payload?.text || '';
                if (typeof payloadText === 'string' && payloadText.trim()) {
                    return payloadText.trim();
                }

                if (latestCopilotAnswerRef.current) {
                    return latestCopilotAnswerRef.current.trim();
                }

                if (chat) {
                    const assistantMessages = Object.values(chat).filter(
                        (item) => item?.role === 'assistant' && typeof item?.content === 'string',
                    );

                    const latestSummary = assistantMessages.at(-1)?.content?.trim();
                    if (latestSummary) return latestSummary;
                }

                const { chat: fallbackChat } = getChatState({ chatId }) || {};
                if (!fallbackChat) return '';

                const assistantMessages = Object.values(fallbackChat).filter(
                    (item) => item?.role === 'assistant' && typeof item?.content === 'string',
                );

                return assistantMessages.at(-1)?.content?.trim() || '';
            },
            [chat, chatId, getChatState],
        );

        const onFinishGoldenMinute = useCallback(
            (payload) => {
                const trimmedText = resolveSummaryText(payload);
                if (trimmedText) updateGoldenMinuteLocally(trimmedText);

                dispatchLocal({
                    type: WIDGET_ACTIONS.FINISH_GOLDEN_MINUTE,
                    payload: { text: trimmedText },
                });

                if (!disableChatInput) {
                    dispatchLocal({
                        type: WIDGET_ACTIONS.SET_CHAT_PROPS,
                        payload: defaultChatProps,
                    });
                    getDanaChatSuggestions();
                }

                latestCopilotAnswerRef.current = '';
                goldenMinuteDebugger(payload);
            },
            [
                updateGoldenMinuteLocally,
                disableChatInput,
                goldenMinuteDebugger,
                defaultChatProps,
                getDanaChatSuggestions,
                resolveSummaryText,
            ],
        );

        const onErrorGoldenMinute = useCallback(() => {
            dispatchLocal({ type: WIDGET_ACTIONS.ERROR_GOLDEN_MINUTE });
            dispatchLocal({ type: WIDGET_ACTIONS.SET_CHAT_PROPS, payload: defaultChatProps });
        }, [defaultChatProps]);

        const onRefreshGoldenMinute = useCallback(() => {
            if (!entity?.trueName || !id) return null;
            clearChat({ chatId });
            dispatchLocal({
                type: WIDGET_ACTIONS.SET_CHAT_PROPS,
                payload: {
                    ...defaultChatProps,
                    defaultFirstInteraction: null,
                    interactions: [
                        {
                            item: {
                                content: {
                                    entity: entity.trueName,
                                    entityId: id,
                                },
                                type: COPILOT_CHAT_INTERACTION_TYPES.PARAMS,
                            },
                        },
                    ],
                    session: {
                        features: [COPILOT_CHAT_FEATURES.ENTITY_SUMMARY],
                    },
                    cb: {
                        onFinish: onFinishGoldenMinute,
                        onError: onErrorGoldenMinute,
                    },
                },
            });
            dispatchLocal({ type: WIDGET_ACTIONS.RESET_FOR_REFRESH });

            logEvent({
                event: entity.trueName,
                submodule: 'danaAssistant',
                functionality: data?.goldenMinuteSummary ? 'refreshSummary' : tracking?.summary,
            });
        }, [
            entity.trueName,
            id,
            clearChat,
            chatId,
            defaultChatProps,
            onFinishGoldenMinute,
            onErrorGoldenMinute,
            data?.goldenMinuteSummary,
            tracking?.summary,
        ]);

        const onOpenDanaChat = useCallback(() => {
            dispatchLocal({ type: WIDGET_ACTIONS.OPEN_MODAL });
            if (shouldShowSuggestions && !suggestions?.length) {
                getDanaChatSuggestions();
            }
        }, [getDanaChatSuggestions, shouldShowSuggestions, suggestions]);

        const onConfirmDisclaimerSuccess = useCallback(
            ({ copilotModalAfterDisclaimer }) => {
                switch (copilotModalAfterDisclaimer) {
                    case OPEN_COPILOT_CHAT:
                        onOpenDanaChat();
                        break;
                    case REFRESH_GOLDEN_MINUTE:
                        onRefreshGoldenMinute();
                        break;
                    default:
                        break;
                }
            },
            [onOpenDanaChat, onRefreshGoldenMinute],
        );

        const {
            copilotActivatedDisclaimer,
            copilotDisclaimerOnClose,
            hasCopilotDebugTools,
            renderCopilotDisclaimer,
            setCopilotModalAfterDisclaimer,
        } = useCopilot({
            onConfirmDisclaimerSuccess,
        });

        // ========================================
        // EFFECTS
        // ========================================
        // Update debug mode when hasCopilotDebugTools is available (only once)
        useEffect(() => {
            if (hasCopilotDebugTools && !hasInitializedDebugMode.current) {
                hasInitializedDebugMode.current = true;
                dispatchLocal({ type: WIDGET_ACTIONS.SET_DEBUG_MODE, payload: true });
            }
        }, [hasCopilotDebugTools]);

        useEffect(() => {
            if (!latestFinishedAssistantSummary || latestFinishedAssistantSummary === textWidget)
                return;

            updateGoldenMinuteLocally(latestFinishedAssistantSummary);
            dispatchLocal({
                type: WIDGET_ACTIONS.FINISH_GOLDEN_MINUTE,
                payload: { text: latestFinishedAssistantSummary },
            });
            latestCopilotAnswerRef.current = '';

            if (!disableChatInput) getDanaChatSuggestions();
        }, [
            disableChatInput,
            getDanaChatSuggestions,
            latestFinishedAssistantSummary,
            textWidget,
            updateGoldenMinuteLocally,
        ]);

        const handleOpenDanaChat = useCallback(() => {
            if (copilotActivatedDisclaimer) {
                onOpenDanaChat();
            } else {
                setCopilotModalAfterDisclaimer(OPEN_COPILOT_CHAT);
            }
        }, [copilotActivatedDisclaimer, onOpenDanaChat, setCopilotModalAfterDisclaimer]);

        const handleRefreshGoldenMinute = useCallback(() => {
            if (copilotActivatedDisclaimer) {
                onRefreshGoldenMinute();
            } else {
                setCopilotModalAfterDisclaimer(REFRESH_GOLDEN_MINUTE);
            }
        }, [copilotActivatedDisclaimer, onRefreshGoldenMinute, setCopilotModalAfterDisclaimer]);

        const getWidgetCommentBoxRef = useCallback(({ ref }) => {
            if (!ref) return;
            widgetCommentBoxRef.current = ref;
        }, []);

        // ========================================
        // RENDER HELPERS
        // ========================================
        const testingTools = useMemo(() => {
            if (!hasCopilotDebugTools) return null;
            return (
                <Tooltip content="Toggle debug mode">
                    <div
                        className={classNames('copilot-golden-minute-testing-tools', {
                            'copilot-golden-minute-testing-tools--active': isDebugMode,
                        })}
                    >
                        <Text
                            type="caption"
                            color={
                                isDebugMode
                                    ? theme.colors.semantic.positive500
                                    : theme.colors.semantic.negative500
                            }
                        >
                            Debug
                        </Text>

                        <Switch
                            checked={isDebugMode}
                            onChange={(value) =>
                                dispatchLocal({
                                    type: WIDGET_ACTIONS.SET_DEBUG_MODE,
                                    payload: value,
                                })
                            }
                        />
                    </div>
                </Tooltip>
            );
        }, [hasCopilotDebugTools, isDebugMode, theme]);

        const lastUpdateDate = useMemo(() => {
            if (!textWidget && !isLoadingGoldenMinute) return null;

            const momentDate = moment(data.goldenMinuteDate, 'DD/MM/YYYY HH:mm:ss');
            const lastUpdateText = isToday(momentDate)
                ? getLiteral('common_today')
                : momentDate.format('L');
            const dateText = getLiteralWithParameters('label_dana_golden_minute_last_update', [
                lastUpdateText,
            ]);

            return (
                <div className="copilot-golden-minute-header">
                    {testingTools}
                    <Text
                        className="copilot-golden-minute-header__update-date"
                        type="caption"
                        color={theme.colors.utility.textSecondary}
                    >
                        {dateText}
                    </Text>
                </div>
            );
        }, [theme, data.goldenMinuteDate, textWidget, isLoadingGoldenMinute, testingTools]);

        const displaySuggestions = useMemo(() => {
            if (!shouldShowSuggestions) return false;
            return isLoadingSuggestions || suggestions?.length > 0;
        }, [isLoadingSuggestions, shouldShowSuggestions, suggestions?.length]);

        const chatSuggestions = useMemo(
            () =>
                displaySuggestions ? (
                    <Suggestions
                        suggestions={suggestions}
                        onClick={(prompt) => featureMethods[`chat-${chatId}`].onSubmit(prompt)}
                        isLoading={isLoadingSuggestions}
                    />
                ) : null,
            [displaySuggestions, suggestions, isLoadingSuggestions, featureMethods, chatId],
        );

        const handleClickCTA = useCallback(
            ({ prompt, trackingFunctionality }) => {
                if (!prompt) return;

                const finalChatProps = {
                    ...defaultChatProps,
                    interactions: [
                        ...defaultChatProps.interactions,
                        {
                            item: {
                                type: COPILOT_CHAT_INTERACTION_TYPES.MESSAGE,
                                content: {
                                    type: 'input_text',
                                    text: prompt,
                                },
                            },
                        },
                    ],
                };

                clearChat({ chatId });
                dispatchLocal({ type: WIDGET_ACTIONS.SET_SUGGESTIONS, payload: [] });
                dispatchLocal({ type: WIDGET_ACTIONS.SET_CHAT_PROPS, payload: finalChatProps });
                handleOpenDanaChat();

                !!trackingFunctionality &&
                    logEvent({
                        event: entity.trueName,
                        submodule: 'danaAssistant',
                        functionality: trackingFunctionality,
                    });
            },
            [chatId, clearChat, defaultChatProps, entity.trueName, handleOpenDanaChat],
        );

        const widgetActions = useMemo(() => {
            if (!textWidget && !isLoadingGoldenMinute) return null;

            const commonProps = {
                type: 'promotion',
                size: 'big',
                iconPosition: 'left',
                className: 'copilot-golden-minute-actions__button',
            };

            const showRefreshButton =
                isError || (textWidget && isOutdated && !isLoadingGoldenMinute);

            const buttonSchema = [
                showRefreshButton &&
                    actionsConfig?.showRefresh && {
                        icon: 'refresh',
                        children: getLiteral('label_copilot_cta_update_summary'),
                        onClick: handleRefreshGoldenMinute,
                    },
                !showRefreshButton &&
                    actionsConfig?.showViewSummary &&
                    literals?.viewSummary && {
                        children: literals?.viewSummary,
                        onClick: handleOpenDanaChat,
                    },
                ...(actionsConfig?.customActions || []).map((action) => ({
                    icon: action.icon,
                    children: action.text,
                    onClick: () =>
                        handleClickCTA({
                            prompt: action.onClickPayload?.prompt,
                            trackingFunctionality: action.onClickPayload?.trackingFunctionality,
                        }),
                })),
            ].filter(Boolean);

            return (
                <div className="copilot-golden-minute-actions">
                    {buttonSchema.map((button, index) => (
                        <Button key={`${button.icon}-${index}`} {...commonProps} {...button} />
                    ))}
                </div>
            );
        }, [
            textWidget,
            isLoadingGoldenMinute,
            isError,
            isOutdated,
            actionsConfig?.showRefresh,
            actionsConfig?.showViewSummary,
            actionsConfig?.customActions,
            handleRefreshGoldenMinute,
            literals?.viewSummary,
            handleOpenDanaChat,
            handleClickCTA,
        ]);

        const modalTitle = useMemo(() => {
            if (!data?.name) return null;
            return (
                <Text
                    color={theme.colors.utility.textSecondary}
                    className="dana-chat-modal__assistantFor"
                    isTruncated
                >{`${getLiteral('label_assistant_for')} ${data.name}`}</Text>
            );
        }, [data?.name, theme.colors.utility.textSecondary]);

        const handleModalClose = useCallback(() => {
            // Cancel the stream reader if it's not an account summary chat
            const reader = getStreamReader({ chatId });
            if (
                reader &&
                !chatProps.session.features.includes(COPILOT_CHAT_FEATURES.ENTITY_SUMMARY)
            )
                reader.cancel();
            dispatchLocal({ type: WIDGET_ACTIONS.CLOSE_MODAL });
            copilotDisclaimerOnClose();
        }, [chatId, chatProps.session.features, copilotDisclaimerOnClose, getStreamReader]);

        const onClearChat = useCallback(() => {
            dispatchLocal({ type: WIDGET_ACTIONS.SET_SUGGESTIONS, payload: [] });
            dispatchLocal({ type: WIDGET_ACTIONS.SET_CHAT_PROPS, payload: defaultChatProps });
        }, [defaultChatProps]);

        const copilotModalProps = useMemo(() => {
            const customCallbacks = chatProps?.cb || {};
            const mergedCallbacks = {
                ...customCallbacks,
                onAnswer: (payload) => {
                    goldenMinuteDebugger(payload);
                    const answerText = payload?.text;
                    if (typeof answerText === 'string' && answerText.trim()) {
                        latestCopilotAnswerRef.current = answerText.trim();
                    }
                    customCallbacks?.onAnswer?.(payload);
                },
                onArtifact: (payload) => {
                    goldenMinuteDebugger(payload);
                    customCallbacks?.onArtifact?.(payload);
                },
                onData: (payload) => {
                    goldenMinuteDebugger(payload);
                    customCallbacks?.onData?.(payload);
                },
                onError: (payload) => {
                    goldenMinuteDebugger(payload);
                    customCallbacks?.onError?.(payload);
                },
                onFinish: (payload) => {
                    goldenMinuteDebugger(payload);
                    customCallbacks?.onFinish?.(payload);
                },
                onClearChat: (...args) => {
                    onClearChat(...args);
                    customCallbacks?.onClearChat?.(...args);
                },
            };

            return {
                chatSetup: {
                    ...chatProps,
                    chatId,
                    chatSuggestions,
                    locale: userData.locale,
                    handleEmail: canUseEmail ? mailContent : undefined,
                    handleFeedback: ChatGPTService.updateDanaChatFeedback,
                    handleVote: ChatGPTService.sendDanaChatFeedback,
                    isError,
                    cb: mergedCallbacks,
                },
                metadata,
                open: isOpen,
                onClose: handleModalClose,
                title: modalTitle,
            };
        }, [
            canUseEmail,
            chatId,
            chatProps,
            chatSuggestions,
            goldenMinuteDebugger,
            handleModalClose,
            isError,
            isOpen,
            mailContent,
            modalTitle,
            onClearChat,
            userData.locale,
        ]);

        const widgetLayoutProps = useMemo(
            () => ({
                className: classNames('copilot-golden-minute', {
                    'copilot-golden-minute--hasDebug': hasCopilotDebugTools,
                }),
                data: {},
                dataLessWidget: true,
                id: 'copilot-golden-minute',
                isDynamic: true,
                loading,
                placeholderHeight: 120,
                setSize,
                type: 'comments',
            }),
            [hasCopilotDebugTools, loading, setSize],
        );

        const commentProps = useMemo(
            () => ({
                avatar: metadata.SageCopilotIcon,
                creationDate: data.goldenMinuteDate,
                getChatBoxRef: getWidgetCommentBoxRef,
                isWidget: true,
                onClick: handleOpenDanaChat,
                text: textWidget,
            }),
            [data.goldenMinuteDate, getWidgetCommentBoxRef, handleOpenDanaChat, textWidget],
        );

        // ========================================
        // RENDER
        // ========================================
        return (
            <WidgetLayout {...widgetLayoutProps}>
                <div className="copilot-golden-minute__custom-header">
                    <div className="copilot-golden-minute-title">
                        <Text type="subtitle" medium isTruncated>
                            {getLiteral('label_danai_golderminute_title')}
                        </Text>
                        <SageCopilot />
                        <BetaBadge />
                    </div>
                    {lastUpdateDate}
                </div>
                <WidgetContentLayout>
                    <div className="copilot-golden-minute-content">
                        {isError && <ErrorScreen size="small" />}
                        {!isError && isLoadingGoldenMinute && (
                            <CopilotLoader src={metadata.danaLoaderLottie} />
                        )}
                        {!isError && !isLoadingGoldenMinute && !textWidget && (
                            <EmptySummary
                                onRefreshGoldenMinute={handleRefreshGoldenMinute}
                                literals={literals}
                            />
                        )}
                        {!isError && !isLoadingGoldenMinute && textWidget && (
                            <>
                                <Comment {...commentProps} />
                                {widgetActions}
                            </>
                        )}
                    </div>
                </WidgetContentLayout>
                <CopilotModal {...copilotModalProps} />
                {renderCopilotDisclaimer}
            </WidgetLayout>
        );
    },
);

GoldenMinuteWidget.displayName = 'GoldenMinuteWidget';

export default GoldenMinuteWidget;
